import { useEffect, useRef } from "react";

const EmberCanvas = ({ density = 28, firebed = false, className = "" }) => {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    let w = 0;
    let h = 0;
    let raf;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      w = canvas.offsetWidth;
      h = canvas.offsetHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const spawn = (anyY) => ({
      x: Math.random() * (w || 1200),
      y: anyY ? Math.random() * (h || 800) : (h || 800) + 12,
      r: 0.5 + Math.random() * 1.7,
      vy: (0.12 + Math.random() * 0.4) * (firebed ? 1.6 : 1),
      amp: 8 + Math.random() * 26,
      freq: 0.6 + Math.random() * 1.2,
      phase: Math.random() * Math.PI * 2,
      alpha: 0.12 + Math.random() * 0.45,
      pulse: 0.4 + Math.random() * 1.4,
      warm: Math.random() < 0.55,
    });

    const embers = Array.from({ length: density }, () => spawn(true));

    let t = 0;

    // Pre-rendered soft glow sprites — fire has no hard edges, so each flame
    // particle is a blurred radial blob tinted by heat stage.
    const mkSprite = (inner, mid) => {
      const c = document.createElement("canvas");
      c.width = c.height = 64;
      const g = c.getContext("2d");
      const grad = g.createRadialGradient(32, 32, 0, 32, 32, 32);
      grad.addColorStop(0, inner);
      grad.addColorStop(0.45, mid);
      grad.addColorStop(1, "rgba(0, 0, 0, 0)");
      g.fillStyle = grad;
      g.fillRect(0, 0, 64, 64);
      return c;
    };
    const flameSprites = [
      mkSprite("rgba(255, 246, 214, 0.95)", "rgba(255, 186, 90, 0.42)"),
      mkSprite("rgba(255, 178, 66, 0.85)", "rgba(255, 116, 30, 0.34)"),
      mkSprite("rgba(233, 76, 26, 0.7)", "rgba(168, 34, 16, 0.22)"),
      mkSprite("rgba(150, 30, 16, 0.45)", "rgba(70, 12, 8, 0.12)"),
    ];

    // Drifting hot spots make flames clump naturally instead of spacing evenly.
    const hotspots = Array.from({ length: 5 }, () => ({
      x: 0.08 + Math.random() * 0.84,
      phase: Math.random() * Math.PI * 2,
      speed: 0.12 + Math.random() * 0.22,
      width: 0.05 + Math.random() * 0.09,
      heat: 0.6 + Math.random() * 0.4,
    }));

    const spawnFlame = () => {
      const hs = hotspots[(Math.random() * hotspots.length) | 0];
      const drift = Math.sin(t * hs.speed + hs.phase) * 0.05;
      const cx = (hs.x + drift) * w;
      return {
        x: cx + (Math.random() - 0.5) * hs.width * w * 2,
        y: h + 10 + Math.random() * 8,
        vx: (Math.random() - 0.5) * 0.35,
        vy: (0.9 + Math.random() * 1.9) * hs.heat,
        life: 1,
        decay: 0.007 + Math.random() * 0.012,
        size: 22 + Math.random() * 38,
        swayPhase: Math.random() * Math.PI * 2,
        swayFreq: 1.2 + Math.random() * 2.4,
      };
    };
    const flames = Array.from({ length: 230 }, () => {
      const p = spawnFlame();
      p.y = h - Math.random() * h * 0.3;
      p.life = 0.35 + Math.random() * 0.65;
      return p;
    });

    const drawFirebed = () => {
      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      const flicker = 0.82 + 0.18 * Math.sin(t * 2.9) + 0.07 * Math.sin(t * 7.1);

      const band = ctx.createLinearGradient(0, h - 130, 0, h);
      band.addColorStop(0, "rgba(229, 46, 46, 0)");
      band.addColorStop(1, `rgba(255, 96, 22, ${0.26 * flicker})`);
      ctx.fillStyle = band;
      ctx.fillRect(0, h - 130, w, 130);

      for (const p of flames) {
        p.life -= p.decay;
        p.y -= p.vy * (0.45 + p.life * 0.8);
        p.x += p.vx + Math.sin(t * p.swayFreq + p.swayPhase) * 0.5;
        if (p.life <= 0.02 || p.y < h * 0.48) Object.assign(p, spawnFlame());
        const stage = p.life > 0.72 ? 0 : p.life > 0.46 ? 1 : p.life > 0.22 ? 2 : 3;
        const s = p.size * (0.55 + p.life * 0.85);
        ctx.globalAlpha = Math.min(Math.max((p.life - 0.04) * 0.7, 0), 0.55);
        ctx.drawImage(flameSprites[stage], p.x - s / 2, p.y - s / 2, s, s);
      }
      ctx.globalAlpha = 1;
      ctx.restore();
    };

    const tick = () => {
      t += 0.016;
      ctx.clearRect(0, 0, w, h);
      if (firebed) drawFirebed();
      for (const e of embers) {
        e.y -= e.vy;
        const x = e.x + Math.sin(t * e.freq + e.phase) * e.amp * 0.15;
        const a = e.alpha * (0.55 + 0.45 * Math.sin(t * e.pulse + e.phase));
        const color = e.warm ? "229, 46, 46" : "255, 100, 20";
        ctx.beginPath();
        ctx.arc(x, e.y, e.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${color}, ${Math.max(a, 0)})`;
        ctx.shadowBlur = 8;
        ctx.shadowColor = `rgba(${color}, 0.7)`;
        ctx.fill();
        if (e.y < -14) Object.assign(e, spawn(false));
      }
      raf = requestAnimationFrame(tick);
    };
    tick();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, [density, firebed]);

  return <canvas ref={ref} className={className} aria-hidden="true" />;
};

export default EmberCanvas;
